import { defineStore } from "pinia";
import cornerstoneDICOMImageLoader from "@cornerstonejs/dicom-image-loader";

export const useKeyImageStore = defineStore("keyImages", {
  state: () => ({
    markedImages: {},
    showIndicators: true,
  }),

  getters: {
    totalMarked() {
      return Object.keys(this.markedImages).length;
    },
    allMarked() {
      return Object.entries(this.markedImages)
        .map(([imageId, data]) => ({ imageId, ...data }))
        .sort((a, b) => new Date(a.markedAt) - new Date(b.markedAt));
    },
  },

actions: {
  markCurrentImage(viewport, options = {}) {
    if (!viewport?.imageIds) return;

    const currentIndex = viewport.getTargetImageIdIndex();
    const imageId = viewport.imageIds[currentIndex];
    const metadata = cornerstoneDICOMImageLoader.wadors.metaDataManager.get(imageId);

    this.markedImages[imageId] = {
      studyUID: metadata?.["0020000D"]?.Value?.[0] || "",
      seriesUID: metadata?.["0020000E"]?.Value?.[0] || "",
      instanceUID: metadata?.["00080018"]?.Value?.[0] || "",
      sopClassUID: metadata?.["00080016"]?.Value?.[0] || "",
      imageIndex: currentIndex,
      instanceNumber: metadata?.["00200013"]?.Value?.[0] || currentIndex + 1,
      note: options.note || "",
      category: options.category || "finding",
      markedAt: new Date().toISOString(),
    };
  },

  unmarkImage(imageId) {
    delete this.markedImages[imageId];
  },

  toggleMark(viewport, options = {}) {
    if (!viewport?.imageIds) return false;

    const currentIndex = viewport.getTargetImageIdIndex();
    const imageId = viewport.imageIds[currentIndex];

    if (this.markedImages[imageId]) {
      this.unmarkImage(imageId);
      return false;
    }

    this.markCurrentImage(viewport, options);
    return true;
  },

  isMarked(imageId) {
    return !!this.markedImages[imageId];
  },

  isCurrentImageMarked(viewport) {
    if (!viewport?.imageIds) return false;
    return this.isMarked(viewport.imageIds[viewport.getTargetImageIdIndex()]);
  },

  getMarkedForStudy(studyUID) {
    return this.allMarked.filter((m) => m.studyUID === studyUID);
  },

  getMarkedForSeries(seriesUID) {
    return this.allMarked.filter((m) => m.seriesUID === seriesUID);
  },

  getMarkedIndices(imageIds) {
    return imageIds
      .map((id, index) => (this.isMarked(id) ? index : -1))
      .filter((i) => i >= 0);
  },

  navigateToKeyImage(viewport, direction = "next") {
    if (!viewport?.imageIds) return false;

    const currentIndex = viewport.getTargetImageIdIndex();
    const markedIndices = this.getMarkedIndices(viewport.imageIds);
    if (markedIndices.length === 0) return false;

    let targetIndex;
    if (direction === "next") {
      targetIndex = markedIndices.find((i) => i > currentIndex);
      if (targetIndex === undefined) targetIndex = markedIndices[0];
    } else {
      const reversed = [...markedIndices].reverse();
      targetIndex = reversed.find((i) => i < currentIndex);
      if (targetIndex === undefined) targetIndex = reversed[0];
    }

    viewport.setImageIdIndex(targetIndex);
    return true;
  },

  clearAll() {
    this.markedImages = {};
  },
}
});
